import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { ownUsageSchema } from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { referenceClient } from "@/lib/orpc/client";
import { queryClient } from "@/lib/query-client";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";

const orpc = createTanstackQueryUtils(referenceClient, {
  scoped: {
    ownUsage: {
      queryInterceptors: [
        async (options) => ownUsageSchema.parse(await options.next()),
      ],
    },
  },
});
const key = (scope: VisitScope): readonly unknown[] => [
  "account-usage",
  scope.uid,
  scope.accountPublicId,
  scope.generation,
];
const disabledScope: VisitScope = {
  uid: "",
  accountPublicId: "",
  generation: 0,
};
export const usageOptions = (scope: VisitScope) =>
  orpc.ownUsage.queryOptions({
    queryKey: key(scope),
    input: {},
    context: { expectedUserUid: scope.uid },
    staleTime: 30_000,
    retry: false,
  });
export async function refreshOwnUsage(
  scope: VisitScope | null = getVisitScope(),
): Promise<void> {
  if (!scope || getVisitScope() !== scope) return;
  // Acceptance must not reuse a balance read that started before spending.
  await queryClient.cancelQueries({ queryKey: key(scope) });
  if (getVisitScope() !== scope) return;
  return queryClient.invalidateQueries(
    { queryKey: key(scope) },
    { cancelRefetch: false },
  );
}
export function useOwnUsage(enabled = true) {
  const scope = useVisitScope();
  const query = useQuery({
    ...usageOptions(scope ?? disabledScope),
    enabled: enabled && !!scope,
  });
  const refresh = useCallback(() => refreshOwnUsage(scope), [scope]);
  return {
    data: query.isError ? undefined : query.data,
    loading: enabled && !!scope && (query.isPending || query.isFetching),
    error: query.isError,
    refresh,
  };
}
